/**
 * Two-factor on Profile — every account's, personal ones too. On: how many
 * recovery codes are left, new ones, and turning it off (refused, and said so,
 * while an organization requires it). Off: setting it up.
 */
import { useCallback, useEffect, useState } from 'react';
import type React from 'react';
import { color } from '../../theme/tokens';
import { Card, Field, GhostButton, Input, PrimaryButton } from '../../components/primitives';
import { api, ApiError } from '../../lib/api';
import { TwoFactorSetup } from './TwoFactorSetup';
import { RecoveryCodes } from './two-factor-parts';

interface Status {
  enabled: boolean;
  recoveryLeft: number;
  configured: boolean;
  requiredBy: string[];
}

export function TwoFactorCard(): React.JSX.Element {
  const [status, setStatus] = useState<Status | null>(null);
  const [setting, setSetting] = useState(false);
  const [code, setCode] = useState('');
  const [codes, setCodes] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setStatus(await api.get<Status>('/me/2fa'));
  }, []);
  useEffect(() => {
    void load().catch(() => undefined);
  }, [load]);
  const start = useCallback(() => api.post<{ secret: string; uri: string }>('/me/2fa/setup'), []);
  const enable = useCallback(async (typed: string) => (await api.post<{ recoveryCodes: string[] }>('/me/2fa/enable', { code: typed })).recoveryCodes, []);

  const act = async (path: string): Promise<void> => {
    setError(null);
    try {
      const answer = await api.post<Status & { recoveryCodes?: string[] }>(path, { code: code.trim() });
      setCodes(answer.recoveryCodes ?? null);
      setCode('');
      await load();
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : 'That did not work.');
    }
  };

  return (
    <Card>
      <div style={{ fontSize: 16, fontWeight: 600 }}>Two-factor authentication</div>
      {status === null ? null : setting ? (
        <div style={{ marginTop: 16 }}>
          <TwoFactorSetup
            start={start}
            enable={enable}
            onDone={() => {
              setSetting(false);
              void load();
            }}
          />
        </div>
      ) : !status.enabled ? (
        <>
          <p style={{ fontSize: 13.5, color: color.inkSubtle, margin: '8px 0 16px', lineHeight: 1.6 }}>
            A code from your phone after your password or GitHub.
            {!status.configured && ' This deployment cannot set it up yet.'}
          </p>
          {status.configured && <GhostButton onClick={() => setSetting(true)}>Set it up</GhostButton>}
        </>
      ) : (
        <>
          <p style={{ fontSize: 13.5, color: color.inkSubtle, margin: '8px 0 16px', lineHeight: 1.6 }}>
            On. {status.recoveryLeft} recovery code{status.recoveryLeft === 1 ? '' : 's'} left.
            {status.requiredBy.length > 0 && ` ${status.requiredBy.join(', ')} requires it.`}
          </p>
          {codes && (
            <div style={{ marginBottom: 14 }}>
              <RecoveryCodes codes={codes} />
            </div>
          )}
          <Field label="A current code, to change anything here" htmlFor="tfa-profile-code">
            <Input id="tfa-profile-code" autoComplete="one-time-code" value={code} onChange={(event) => setCode(event.target.value)} />
          </Field>
          <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
            <PrimaryButton tone="ink" size="sm" onClick={() => void act('/me/2fa/recovery')}>
              New recovery codes
            </PrimaryButton>
            {status.requiredBy.length === 0 && <GhostButton size="sm" onClick={() => void act('/me/2fa/disable')}>Turn it off</GhostButton>}
          </div>
        </>
      )}
      {error && <p role="alert" style={{ margin: '12px 0 0', fontSize: 13.5, color: color.badText }}>{error}</p>}
    </Card>
  );
}
